// ============================================================================
// Pipeline — today's board in its new place (prototype screen 5): the three
// stages read from the sub's own status, labelled Lead · Interviewing · Bench.
// Drag unchanged, market selector unchanged, a card tap opens a detail sheet
// with one button, "Open full profile". Nothing else.
// Any account manager may move a card (TCSD-0373 ¶3); the bench is nightly only.
// ============================================================================

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState, ErrorState, Skeleton, useToast } from '../../../shared/components';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
import { marketOptions, useMarkets } from '../../../shared/markets';
import { SourcingKanban, type PipelineCardRow } from '../../sourcing/kanban';
import { isPipelineStage, subStatusLabel, type PipelineStage } from '../stages';
import { shortMarkets, type HubSub } from './use-hub-data';
import { benchRefusal, pipelineMoveExtra, setSubStatus, workLabel } from './actions';
import { Sheet, bigSecondary } from './sheet';
import { applicationAnswers } from '../../sourcing-intake/answers';
import { focusWhenReady } from '../../home/home-work';

interface Detail {
  promoted_provider_id: string;
  channel: string | null;
  intake_completed: boolean;
  intake_json: unknown;
  created_at: string;
}

function daysAgo(iso: string): string {
  const d = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
  return d === 0 ? 'today' : `${d}d ago`;
}

export function PipelineTab({ subs, loading, error, refetch }: { subs: HubSub[]; loading: boolean; error: string | null; refetch: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { markets } = useMarkets();
  const [market, setMarket] = useState('');
  const [details, setDetails] = useState<Map<string, Detail>>(new Map());
  const [detailsFailed, setDetailsFailed] = useState(false);
  const [moved, setMoved] = useState<Map<string, PipelineStage>>(new Map());
  const [open, setOpen] = useState<HubSub | null>(null);

  const pipeline = useMemo(() => subs.filter((s) => isPipelineStage(s.status)), [subs]);

  useEffect(() => {
    const ids = pipeline.map((s) => s.provider_id);
    if (ids.length === 0) return;
    void (async () => {
      const { data, error: readError } = await supabase
        .from('sourcing_prospects')
        .select('promoted_provider_id, channel, intake_completed, intake_json, created_at')
        .in('promoted_provider_id', ids)
        // Oldest first, so the newest application is the one the map keeps — the
        // same "latest application" the profile reads.
        .order('created_at', { ascending: true });
      // A failed read must not read as "Not back yet" — say it could not be checked.
      setDetailsFailed(Boolean(readError));
      setDetails(new Map(((data ?? []) as Detail[]).map((d) => [d.promoted_provider_id, d])));
    })();
  }, [pipeline]);

  useEffect(() => setMoved(new Map()), [subs]);

  const rows: PipelineCardRow[] = pipeline
    .filter((s) => !market || s.markets.includes(market))
    .map((s) => {
      const d = details.get(s.provider_id);
      const app = d ? (d.intake_completed ? `applied ${daysAgo(d.created_at)}` : `added ${daysAgo(d.created_at)} · application not back`) : `added ${daysAgo(s.created_at)}`;
      return {
        id: s.provider_id,
        name: s.name,
        stage: moved.get(s.provider_id) ?? (s.status as PipelineStage),
        sub: [app, d?.channel && d.channel !== 'AM' ? `via ${d.channel}` : null, shortMarkets(s.markets)].filter(Boolean).join(' · '),
      };
    });

  async function onStageChange(row: PipelineCardRow, next: PipelineStage) {
    const s = subs.find((x) => x.provider_id === row.id);
    if (!s) return;
    const refused = next === 'Ready' ? benchRefusal(s) : null;
    if (refused) {
      toast.error(`${s.name} can't go on the bench`, { description: refused });
      return;
    }
    setMoved((m) => new Map(m).set(row.id, next));
    try {
      await setSubStatus(row.id, next, pipelineMoveExtra(next, s));
      toast.success(`${s.name} → ${subStatusLabel(next)}`);
      refetch();
    } catch (e) {
      setMoved((m) => {
        const n = new Map(m);
        n.delete(row.id);
        return n;
      });
      toast.error(`Could not move ${s.name}`, { description: errorMessage(e) });
    }
  }

  // "Submitted · N against · Read answers" once the application is back; Read
  // answers opens the profile at its Application section (item 833), landing the
  // way Home lands on a card's section.
  function applicationRow(providerId: string): ReactNode {
    const d = details.get(providerId);
    if (!d && detailsFailed) return "Couldn't check";
    if (!d?.intake_completed) return 'Not back yet';
    const against = applicationAnswers(d.intake_json).countsAgainst;
    return (
      <>
        Submitted{against > 0 ? ` · ${against} against` : ''} ·{' '}
        <button
          type="button"
          className="font-medium text-tcs-text underline"
          onClick={() => {
            navigate(`/people/subcontractors/${providerId}`, { state: { open: 'application' } });
            focusWhenReady('application', undefined, 'start');
          }}
        >
          Read answers
        </button>
      </>
    );
  }

  if (error) return <ErrorState body={error} onRetry={refetch} />;
  if (loading) return <Skeleton className="h-48 w-full" />;

  return (
    <div className="space-y-3">
      <select
        aria-label="Market"
        value={market}
        onChange={(e) => setMarket(e.target.value)}
        className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-sm"
      >
        <option value="">All markets</option>
        {marketOptions(markets).map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      {pipeline.length === 0 ? (
        <EmptyState title="Nobody in the pipeline yet" body="Add one with + Add, or share your application link — everyone who applies lands here as a Lead." />
      ) : (
        <SourcingKanban rows={rows} onSelect={(r) => setOpen(subs.find((s) => s.provider_id === r.id) ?? null)} onStageChange={onStageChange} />
      )}
      <p className="text-xs text-tcs-text-muted">Drag a card between columns; tap it for its details.</p>

      {open && (
        <Sheet title={open.name} label={open.name} onClose={() => setOpen(null)}>
          <dl className="space-y-1 text-sm">
            {([
              ['Stage', subStatusLabel(moved.get(open.provider_id) ?? open.status)],
              ['Markets', shortMarkets(open.markets) || '—'],
              ['Work', workLabel(open.does_nightly, open.does_projects) ?? 'Not known yet'],
              ['Phone', open.phone ?? '—'],
              ['Application', applicationRow(open.provider_id)],
            ] as Array<[string, ReactNode]>).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-tcs-border py-1">
                <dt className="text-tcs-text-muted">{k}</dt>
                <dd className="text-right font-medium text-tcs-text">{v}</dd>
              </div>
            ))}
          </dl>
          <button type="button" className={bigSecondary} onClick={() => navigate(`/people/subcontractors/${open.provider_id}`)}>
            Open full profile
          </button>
        </Sheet>
      )}
    </div>
  );
}
